/** Ключи и хуки TanStack Query по доменам. */

import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query'

import { api, type QueryParams } from './client'
import type {
  AppConfig,
  DashboardLive,
  DashboardSummary,
  DashboardTimeseries,
  DemoScenario,
  EmployeeItem,
  LocationAdminItem,
  LocationItem,
  Me,
  ScenarioBindingItem,
  ScenarioDetail,
  ScenarioListItem,
  ScenarioVersionDetail,
  ScenarioVersionItem,
  ScriptDefinition,
  ScriptStep,
  SessionDetail,
  SessionListResponse,
  SessionReview,
  UserItem,
  UserListResponse,
  Utterance,
} from './types'

export const queryKeys = {
  me: ['me'] as const,
  config: ['config'] as const,
  locations: ['locations'] as const,
  managedLocations: ['locations', 'manage'] as const,
  employees: (includeInactive: boolean) => ['employees', { includeInactive }] as const,
  sessions: (params: QueryParams) => ['sessions', params] as const,
  session: (id: string) => ['session', id] as const,
  transcript: (id: string) => ['transcript', id] as const,
  review: (id: string) => ['review', id] as const,
  summary: (params: QueryParams) => ['dashboard', 'summary', params] as const,
  timeseries: (params: QueryParams) => ['dashboard', 'timeseries', params] as const,
  live: ['dashboard', 'live'] as const,
  scenarios: ['scenarios'] as const,
  scenario: (id: string) => ['scenario', id] as const,
  scenarioVersions: (id: string) => ['scenario', id, 'versions'] as const,
  scenarioVersion: (id: string, version: number) => ['scenario', id, 'versions', version] as const,
  scenarioDefaults: ['scenario-defaults'] as const,
  users: (params: QueryParams) => ['users', params] as const,
  locationBindings: ['scenario-bindings', 'locations'] as const,
  userBindings: ['scenario-bindings', 'users'] as const,
  demo: ['demo'] as const,
}

/* ---------- профиль и справочники ---------- */

export const meQueryOptions = queryOptions({
  queryKey: queryKeys.me,
  queryFn: () => api.get<Me>('/api/me'),
  // Профиль и словари меняются редко — незачем перезапрашивать на каждый экран.
  staleTime: 5 * 60 * 1000,
  retry: false,
})

export const configQueryOptions = queryOptions({
  queryKey: queryKeys.config,
  queryFn: () => api.get<AppConfig>('/api/config'),
  staleTime: 30 * 60 * 1000,
})

export function useConfig() {
  return useQuery(configQueryOptions)
}

export function useLocations(enabled = true, includeArchived = false) {
  return useQuery({
    queryKey: [...queryKeys.locations, { includeArchived }] as const,
    queryFn: () =>
      api.get<LocationItem[]>('/api/locations', {
        include_archived: includeArchived || undefined,
      }),
    staleTime: 10 * 60 * 1000,
    enabled,
  })
}

export function useEmployees(enabled = true, includeInactive = false) {
  return useQuery({
    queryKey: queryKeys.employees(includeInactive),
    queryFn: () =>
      api.get<EmployeeItem[]>('/api/employees', {
        include_inactive: includeInactive || undefined,
      }),
    staleTime: 10 * 60 * 1000,
    enabled,
  })
}

export function useManagedLocations() {
  return useQuery({
    queryKey: queryKeys.managedLocations,
    queryFn: () => api.get<LocationAdminItem[]>('/api/locations/manage'),
  })
}

function invalidateLocationData(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: queryKeys.locations })
  void client.invalidateQueries({ queryKey: ['employees'] })
  void client.invalidateQueries({ queryKey: ['users'] })
}

export function useCreateLocation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { name: string; city: string }) =>
      api.post<LocationItem>('/api/locations', input),
    onSuccess: () => invalidateLocationData(client),
  })
}

export function useUpdateLocation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { name?: string; city?: string } }) =>
      api.patch<LocationItem>(`/api/locations/${id}`, patch),
    onSuccess: () => invalidateLocationData(client),
  })
}

export function useArchiveLocation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/locations/${id}`),
    onSuccess: () => invalidateLocationData(client),
  })
}

export function useRestoreLocation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<LocationItem>(`/api/locations/${id}/restore`),
    onSuccess: () => invalidateLocationData(client),
  })
}

export function useReplaceLocationEmployees() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, employeeIds }: { id: string; employeeIds: string[] }) =>
      api.put<LocationAdminItem>(`/api/locations/${id}/employees`, {
        employee_ids: employeeIds,
      }),
    onSuccess: () => invalidateLocationData(client),
  })
}

/* ---------- разговоры ---------- */

type RefetchInterval<T> = NonNullable<UseQueryOptions<T>['refetchInterval']>

export function useSessions(
  params: QueryParams,
  options?: { refetchInterval?: RefetchInterval<SessionListResponse> },
) {
  return useQuery({
    queryKey: queryKeys.sessions(params),
    queryFn: () => api.get<SessionListResponse>('/api/sessions', params),
    // Держим предыдущую страницу при смене фильтров — таблица не мигает.
    placeholderData: (previous) => previous,
    refetchInterval: options?.refetchInterval,
  })
}

export function useSession(id: string, options?: Partial<UseQueryOptions<SessionDetail>>) {
  return useQuery({
    queryKey: queryKeys.session(id),
    queryFn: () => api.get<SessionDetail>(`/api/sessions/${id}`),
    ...options,
  })
}

export function useTranscript(
  id: string,
  options?: { refetchInterval?: RefetchInterval<Utterance[]> },
) {
  return useQuery({
    queryKey: queryKeys.transcript(id),
    queryFn: () => api.get<Utterance[]>(`/api/sessions/${id}/transcript`),
    refetchInterval: options?.refetchInterval,
  })
}

/** Поллится, пока разбор в статусе pending — вместо перезагрузки страницы. */
export function useReviewPolling(id: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.review(id),
    queryFn: () => api.get<SessionReview>(`/api/sessions/${id}/review`),
    enabled,
    refetchInterval: enabled ? 5000 : false,
    retry: false,
  })
}

export function useStopSession() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<{ status: string }>(`/api/sessions/${id}/stop`),
    onSuccess: (_data, id) => {
      void client.invalidateQueries({ queryKey: queryKeys.session(id) })
      void client.invalidateQueries({ queryKey: ['sessions'] })
      void client.invalidateQueries({ queryKey: queryKeys.live })
    },
  })
}

export function useDeleteSession() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/sessions/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['sessions'] })
      void client.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useRefreshReview() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<{ status: string }>(`/api/sessions/${id}/review/refresh`),
    onSuccess: (_data, id) => {
      void client.invalidateQueries({ queryKey: queryKeys.review(id) })
      void client.invalidateQueries({ queryKey: queryKeys.session(id) })
    },
  })
}

/* ---------- дашборд ---------- */

export function useSummary(params: QueryParams) {
  return useQuery({
    queryKey: queryKeys.summary(params),
    queryFn: () => api.get<DashboardSummary>('/api/dashboard/summary', params),
    placeholderData: (previous) => previous,
  })
}

export function useTimeseries(params: QueryParams) {
  return useQuery({
    queryKey: queryKeys.timeseries(params),
    queryFn: () => api.get<DashboardTimeseries>('/api/dashboard/timeseries', params),
    placeholderData: (previous) => previous,
  })
}

export function useLive(enabled = true) {
  return useQuery({
    queryKey: queryKeys.live,
    queryFn: () => api.get<DashboardLive>('/api/dashboard/live'),
    enabled,
    refetchInterval: enabled ? 10_000 : false,
  })
}

/* ---------- сценарии продаж ---------- */

export function useScenarios() {
  return useQuery({
    queryKey: queryKeys.scenarios,
    queryFn: () => api.get<ScenarioListItem[]>('/api/scenarios'),
  })
}

export function useScenario(id: string) {
  return useQuery({
    queryKey: queryKeys.scenario(id),
    queryFn: () => api.get<ScenarioDetail>(`/api/scenarios/${id}`),
  })
}

export function useScenarioVersions(id: string) {
  return useQuery({
    queryKey: queryKeys.scenarioVersions(id),
    queryFn: () => api.get<ScenarioVersionItem[]>(`/api/scenarios/${id}/versions`),
  })
}

export function useScenarioDefaults(enabled = true) {
  return useQuery({
    queryKey: queryKeys.scenarioDefaults,
    queryFn: () => api.get<{ steps: ScriptStep[] }>('/api/scenarios/defaults'),
    staleTime: 60 * 60 * 1000,
    enabled,
  })
}

export async function fetchScenarioVersion(id: string, version: number) {
  return api.get<ScenarioVersionDetail>(`/api/scenarios/${id}/versions/${version}`)
}

function invalidateScenarios(client: ReturnType<typeof useQueryClient>, id?: string) {
  void client.invalidateQueries({ queryKey: queryKeys.scenarios })
  if (id) {
    void client.invalidateQueries({ queryKey: queryKeys.scenario(id) })
    void client.invalidateQueries({ queryKey: queryKeys.scenarioVersions(id) })
  }
}

export function useCreateScenario() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { name: string; definition: ScriptDefinition }) =>
      api.post<ScenarioDetail>('/api/scenarios', input),
    onSuccess: () => invalidateScenarios(client),
  })
}

export function useSaveScenarioVersion(id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (definition: ScriptDefinition) =>
      api.put<ScenarioDetail>(`/api/scenarios/${id}`, { definition }),
    onSuccess: () => invalidateScenarios(client, id),
  })
}

export function useRenameScenario() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.post<ScenarioListItem>(`/api/scenarios/${id}/rename`, { name }),
    onSuccess: (_data, { id }) => invalidateScenarios(client, id),
  })
}

export function useDuplicateScenario() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.post<ScenarioDetail>(`/api/scenarios/${id}/duplicate`, { name }),
    onSuccess: () => invalidateScenarios(client),
  })
}

export function useActivateScenario() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<ScenarioListItem>(`/api/scenarios/${id}/activate`),
    onSuccess: (_data, id) => invalidateScenarios(client, id),
  })
}

/* ---------- пользователи ---------- */

export function useUsers(params: QueryParams) {
  return useQuery({
    queryKey: queryKeys.users(params),
    queryFn: () => api.get<UserListResponse>('/api/users', params),
    placeholderData: (previous) => previous,
  })
}

interface CreateUserInput {
  login: string
  password: string
  role: string
  full_name: string
  location_id: string | null
}

export function useCreateUser() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateUserInput) => api.post<UserItem>('/api/users', input),
    onSuccess: () => invalidateLocationData(client),
  })
}

/**
 * Пропущенное поле бэкенд трактует как «не менять», явный null для точки — как
 * «открепить», поэтому тело собирает вызывающий код, а не хук.
 */
export function useUpdateUser() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api.patch<UserItem>(`/api/users/${id}`, patch),
    onSuccess: () => invalidateLocationData(client),
  })
}

export function useSetUserActive() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.post<UserItem>(`/api/users/${id}/${active ? 'reactivate' : 'deactivate'}`),
    onSuccess: () => invalidateLocationData(client),
  })
}

/** Полностью заменяет набор точек, закреплённых за пользователем. */
export function useSetUserLocations() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, locationIds }: { id: string; locationIds: string[] }) =>
      api.put<UserItem>(`/api/users/${id}/locations`, { location_ids: locationIds }),
    onSuccess: () => invalidateLocationData(client),
  })
}

/* ---------- привязки сценариев к точке/пользователю ---------- */

export function useLocationBindings() {
  return useQuery({
    queryKey: queryKeys.locationBindings,
    queryFn: () => api.get<ScenarioBindingItem[]>('/api/scenario-bindings/locations'),
  })
}

export function useUserBindings() {
  return useQuery({
    queryKey: queryKeys.userBindings,
    queryFn: () => api.get<ScenarioBindingItem[]>('/api/scenario-bindings/users'),
  })
}

function invalidateBindings(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: queryKeys.locationBindings })
  void client.invalidateQueries({ queryKey: queryKeys.userBindings })
}

export function useSetLocationBinding() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ locationId, scenarioId }: { locationId: string; scenarioId: string }) =>
      api.put<ScenarioBindingItem>(`/api/scenario-bindings/locations/${locationId}`, {
        scenario_id: scenarioId,
      }),
    onSuccess: () => invalidateBindings(client),
  })
}

export function useDeleteLocationBinding() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (locationId: string) =>
      api.delete<void>(`/api/scenario-bindings/locations/${locationId}`),
    onSuccess: () => invalidateBindings(client),
  })
}

export function useSetUserBinding() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, scenarioId }: { userId: string; scenarioId: string }) =>
      api.put<ScenarioBindingItem>(`/api/scenario-bindings/users/${userId}`, {
        scenario_id: scenarioId,
      }),
    onSuccess: () => invalidateBindings(client),
  })
}

export function useDeleteUserBinding() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => api.delete<void>(`/api/scenario-bindings/users/${userId}`),
    onSuccess: () => invalidateBindings(client),
  })
}

/* ---------- демо ---------- */

export function useDemoScenarios() {
  return useQuery({
    queryKey: queryKeys.demo,
    queryFn: () => api.get<DemoScenario[]>('/api/demo/scenarios'),
    staleTime: 60 * 60 * 1000,
  })
}

/* ---------- вход и выход ---------- */

export function useLogin() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { login: string; password: string }) =>
      api.post<Me>('/api/auth/session', input),
    onSuccess: (me) => client.setQueryData(queryKeys.me, me),
  })
}

export function useLogout() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete<void>('/api/auth/session'),
    onSuccess: () => client.clear(),
  })
}
